import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { FrontendFieldGuiHint } from 'src/modules/sm-base/models/frontend-field-gui-hint.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Dates } from "src/modules/utils/shared/dates";
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { Objects } from "src/modules/utils/shared/objects";
import { Utils } from 'src/modules/utils/shared/utils';
import { EnaioCertificateService } from '../../services/enaio-certificate.service';
import { ClassLessonPupil } from '../../shared/class-lesson-pupil.entity';
import { ClassLesson } from '../../shared/class-lesson.entity';
import { ClassRegister } from '../../shared/class-register.entity';
import { Pupil } from '../../shared/pupil.model';
import { SickNote } from '../../shared/sick-note.entity';

@Component({
    selector: 'app-class-lesson',
    templateUrl: './class-lesson.component.html',
    styleUrls: ['./class-lesson.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ClassLessonComponent extends ComponentView {

    service = inject(EnaioCertificateService);

    neededParams: ObjectOfSimpleDataTypes = { id: "number", classId: "number?" };
    id: number;
    classId: number;

    classRegister: ClassRegister;
    lesson: ClassLesson;
    sickNotes: SickNote[];
    pupils: Pupil[];
    domains: string[];
    form: FrontendForm;

    pupilRows: PupilRow[];

    async initParams(): Promise<boolean> {
        this.fullScreenComponent = false;
        this.lesson = await RestEndpoint.main().findById(this.id).run("api/cer/classregister/lesson").get(ClassLesson);
        this.classRegister = null;
        if (this.classId > 0) {
            this.classRegister = await this.service.restGetClassRegister(this.classId, null, null, true);
        }
        if (this.classRegister == null) {
            this.classRegister = await this.service.restGetClassRegister(this.lesson.classRegisterIds[0], null, null, true);
        }
        this.pupils = this.lesson.course != null ? await RestEndpoint.main().query({id: this.lesson.course.id}).run("api/cer/classregister/coursepupils").list(Pupil) : await this.service.restGetPupils(this.classRegister.enaioSchoolClass.id);
        let settings = await this.service.restGetClassRegisterSettings(this.classRegister.enaioSchoolClass.getYearNumber(), this.classRegister.isHalfYear);
        this.domains = ["\xa0", ...await this.service.restGetDomains(this.classRegister.enaioSchoolClass.grade, this.classRegister.enaioSchoolClass.getSchoolType(), false, settings, settings)
        ];
        this.sickNotes = await RestEndpoint.main().query({ids: Arrays.itemsToString(this.lesson.classRegisterIds, ";"), courseId: this.lesson.course?.id ?? 0, date: Dates.format(this.lesson.date, "yyyymmdd"), hour: this.lesson.hour}).run("api/cer/classregister/sicknote").list(SickNote);

        this.pupilRows = Arrays.sortBy(this.pupils.map(p => new PupilRow(p, this.lesson.pupils.find(p2 => p2.pupilId == p.id) ?? Objects.fromPlain(ClassLessonPupil, {
            pupilId: p.id
        }), this.sickNotes.find(sn => sn.pupilId == p.id && sn.includes(this.lesson.date, this.lesson.hour)))), p => p.pupil.getClassDisplayName() + "-" + p.pupil.getFullName());

        for (let row of this.pupilRows) {
            if (row.lessonPupil.id == 0 && row.sickNote != null) {
                row.lessonPupil.missing = true;
            }
        }

        this.form = new FrontendForm([
            this.lesson.course == null ? new FrontendField("domain", "Fach", FrontendFieldType.string, { enabled: false }) : null,
            this.lesson.course != null ? new FrontendField("__course", "Kurs", FrontendFieldType.string, { enabled: false }) : null,
            new FrontendField("skipped", "Ausgefallen", FrontendFieldType.bool, {
                guiHints: !this.lesson.skipped || Utils.isNoe(this.lesson.autoSkippedHoliday) ? null : [new FrontendFieldGuiHint("Automatisch aufgrund von Klassenfahrt " + this.lesson.autoSkippedHoliday, "fas fa-info", "green")]
            }),
            new FrontendField("isSubstitute", "Vertretungsstunde", FrontendFieldType.bool),
            new FrontendField("substituteDomain", "Vertretungsfach", FrontendFieldType.dropdown, { 
                listItems: FrontendFieldListItem.make(this.domains)
            }),
            new FrontendField("description", "Dokumentation", FrontendFieldType.textArea, { fixedHeight: "100px"}),
            new FrontendField("homework", "Hausaufgaben", FrontendFieldType.textArea, { fixedHeight: "100px"}),
            new FrontendField("note", "Bemerkung", FrontendFieldType.textArea, { fixedHeight: "100px"}),
            new FrontendField("incidents", "Vorkommnisse", FrontendFieldType.textArea, { fixedHeight: "100px"})
        ]);

        this.form.fill(this.lesson);
        if (this.lesson.course != null) {
            this.form.setValue("__course", this.lesson.course.title);
        }

        this.form.getField("isSubstitute").onValueChanged = this.substituteChanged.bind(this);
        this.substituteChanged();

        this.app.updateNavigation("Klasse " + this.classRegister.enaioSchoolClass.getDisplayName(), [
            { label: "Digitales Klassenbuch", routerLink: ["/enaio-certificates/class-registers/home"] },
            { label: "Klasse " + this.classRegister.enaioSchoolClass.getDisplayName(), routerLink: ["/enaio-certificates", "class-registers", "class-register", this.classRegister.id] },
            { label: "Unterrichtswoche", routerLink: ["/enaio-certificates", "class-registers", "week", this.classRegister.id, Dates.format(Dates.startOf(this.lesson.date, "week"), "yyyymmdd")] },
            { label: this.lesson.getTitle(), routerLink: Arrays.withoutNull(["/enaio-certificates", "class-registers", "lesson", this.id, this.classId]) }
        ]);
        return true;
    }

    substituteChanged(): void {
        let isSubstitute = this.form.getValueBool("isSubstitute");
        this.form.getField("substituteDomain").enabled = isSubstitute;
        if (!isSubstitute) {
            this.form.setValue("substituteDomain", "");
        }
    }

    getNumPresent(): number {
        return this.pupilRows.filter(row => !row.lessonPupil.missing).length;
    }

    async save(): Promise<void> {
        let lesson2 = Objects.cloneDeep(this.lesson);
        this.form.get(lesson2);
        lesson2.pupils = Arrays.withoutNull(this.pupilRows.map(pr => pr.lessonPupil));

        if (!lesson2.missingPupilsChecked && !lesson2.skipped &&
            !await this.app.dialog.yesNo("Sie haben die Abwesenheit noch nicht geprüft. Sie können speichern, die Unterrichtseinheit gilt dann aber noch nicht als abgeschlossen. Fortfahren?", "Warnung")) {
            return;
        }

        await this.app.saveDataHandler(async () => RestEndpoint.main().body(lesson2).post().run("api/cer/classregister/lesson").getText(), this, true, true);
    }

    async addSickNote(row: PupilRow): Promise<void> {
        if (!await this.app.dialog.yesNo("Möchten Sie eine Abmeldung für " + row.pupil.getFullName() + " eintragen?", "Frage")) {
            return;
        }

        let form = this.service.getSickNoteForm(null, true, false);
        let sn = Objects.fromPlain(SickNote, {
            pupilId: row.pupil.id,
            fromDate: this.lesson.date,
            fromHour: this.lesson.hour,
            toDate: this.lesson.date
        });
        form.fill(sn);
        if (await this.app.dialog.form(form, "Abmeldung eintragen", null, true) != "ok") {
            return;
        }
        form.get(sn);

        await RestEndpoint.main().post().query({
            isHalfYear: this.classRegister.isHalfYear,
            classRegisterId: this.classRegister.id
        }).body(sn).run("api/cer/classregister/sicknote").getText();

        this.app.showToast("success", "Abgelegt", "Die Abmeldung wurde abgelegt");

        row.lessonPupil.missing = true;
        row.sickNote = sn;
    }
}

class PupilRow {

    constructor(public pupil: Pupil, public lessonPupil: ClassLessonPupil, public sickNote: SickNote) {
    }
}
